import { React, useState, useEffect } from "react";
import classes from "./DtrDetails.module.css";
import { fontStyles } from "components/Common/commonStyles";
import { useLocation } from "react-router-dom";
import {
  Tab,
  Tabs,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getDTRListClassRosterIdWise } from "services/learnPointUserServices";

import TrainingNotes from "./TrainingNotes";
import HandOffReport from "./HandOffReport";
import Attendance from "./Attendance";
import DTRClassDetails from "./ClassDetails";
import RosterDetails from "./RosterDetails";
import RosterDetailsV2 from "./RosterDetailsV2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "components/AccordionComponents/AccordionComponents";
import applicationUsageLog from "utils/applicationUsagesLog";

import { ThemeProvider } from "@mui/material/styles";
import dtrTheme from "components/LearnPoint/DTR/dtrTheme";
import { getConfigurationValues } from "services/userServices";

export default function DtrDetails() {
  // ASTT-7319 follow-up: was its own third independent createTheme() copy
  // -- same color, but missing the action.disabled override dtrTheme.js
  // already captured from the other two. Found only during the full
  // redesign audit; TrainingNotes.js/HandOffReport.js were the only two
  // fixed in the earlier pass since this parent wasn't in scope yet.
  const theme = dtrTheme;

  useEffect(() => {
    applicationUsageLog(
      localStorage.getItem("User"),
      localStorage.getItem("EmailId"),
      "LearnPoint",
      "DTR Details",
    );
    window.localStorage.removeItem("LPHome");
  }, []);

  const location = useLocation();
  const [expanded, setExpanded] = useState("panel1");
  const [tabIndex, setTabIndex] = useState(0);
  // ASTT-7319 follow-up: null, was "" -- see effectiveRosterId below for
  // why this distinction matters (it's what actually fixes the badge/
  // downstream-components-not-clearing bug).
  const [newClassRosterId, setNewClassRosterId] = useState(null);
  const [classRosterGridV2, setClassRosterGridV2] = useState(false);
  // Resolved once here (from DTRClassDetails' onClassRequestIdChange) and threaded down to
  // TrainingNotes/HandOffReport so attachment uploads don't need their own DB lookup.
  const [classRequestId, setClassRequestId] = useState("");
  // Cast to string once here at the source (downstream code assumes rosterId is a string);
  // "" default keeps the "Roster ID is missing" validation in TrainingNotes working correctly.
  const classRosterId =
    location.state?.classRosterId !== null &&
    location.state?.classRosterId !== undefined
      ? String(location.state.classRosterId)
      : "";
  const dtrDate = location.state?.dtrDate;

  // ASTT-7319 follow-up: the DTR count badge wasn't clearing when the
  // Class Roster Ids selection was cleared -- root cause was
  // `newClassRosterId || classRosterId` used throughout this file (badge
  // count, TrainingNotes/HandOffReport/Attendance/RosterDetails rosterId
  // props, 7 occurrences total). newClassRosterId started as "" and also
  // became "" once the user explicitly cleared their selection --
  // indistinguishable via `||`, so clearing silently fell back to the
  // original classRosterId from the redirect/route state forever, not
  // just for the badge. This wasn't a badge-only bug; every one of those
  // downstream components would have kept the original roster too.
  // Computed once here (newClassRosterId is now initialized to null, not
  // "", specifically so this comparison can tell "never touched" apart
  // from "explicitly cleared") and reused everywhere below instead of
  // repeating the same fallback ternary at each call site.
  const effectiveRosterId =
    newClassRosterId !== null ? newClassRosterId : classRosterId;

  // ASTT-7319: combined DTR count for the currently selected roster(s),
  // shown as a badge on the "Training Notes" tab -- reuses the same
  // endpoint TrainingNotes.js already calls for its calendar highlighting
  // (DTRDate/DTRStatus), just for a count here, not a per-date lookup.
  const [dtrCount, setDtrCount] = useState(0);
  // ASTT-7319 follow-up: DtrDetails.js is the common ancestor of
  // TrainingNotes.js (where Save/Send actually happens) and both the
  // badge count above and ClassDetails.js's Roster Details table/View
  // DTRs list (neither of which live inside TrainingNotes.js) -- this
  // counter is incremented by TrainingNotes.js's onDtrSaved callback
  // after a successful save, and threaded down to whatever needs to
  // refetch as a result, without either side needing to know about the
  // other directly.
  const [dtrRefreshKey, setDtrRefreshKey] = useState(0);
  // ASTT-7178: Hand-Off equivalent of dtrRefreshKey above -- incremented
  // by HandOffReport.js's own onHandOffSaved callback after a successful
  // Save/Send, so ClassDetails.js's roster-status refetch effect can
  // react to it the same way it already reacts to dtrRefreshKey for
  // Training Notes.
  const [handOffRefreshKey, setHandOffRefreshKey] = useState(0);

  // ASTT-7178: lifted up from HandOffReport.js (which used to own this as
  // purely local state) so ClassDetails.js can also read it -- needed to
  // color-code the roster picker by Hand-Off status for the CURRENTLY
  // selected report type. "0"/"1" strings, not numbers, matching the
  // ToggleButtonGroup's own value convention (unchanged from before).
  const [reportType, setReportType] = useState("0");

  // ASTT-7178: lifted up from TrainingNotes.js (which used to own this as
  // purely local state, initialized via the same dtrDate-prop-or-today
  // default it still computes internally) so ClassDetails.js can also
  // read it -- needed to color-code the roster picker by DTR status for
  // the CURRENTLY selected date, mirroring reportType's own lift-up
  // above. "YYYY-MM-DD" string, matching TrainingNotes.js's own
  // convention throughout (selectedDtrDate, dtrStatusByDate keys, etc.)
  // -- kept as a plain local helper here rather than pulling in moment/
  // dayjs just for this one default computation.
  const toIsoDateString = (date) => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const [selectedDtrDate, setSelectedDtrDate] = useState(() =>
    toIsoDateString(dtrDate != null ? dtrDate : new Date()),
  );

  // ASTT-7178: bubbled up from ClassDetails.js's own filterRosterData
  // (which already fetches per-roster Hand-Off/DTR status for coloring)
  // -- lets HandOffReport.js proactively disable Save/Send when the
  // CURRENT selection includes an already-Sent report, without a
  // separate endpoint/call. See ClassDetails.js's own
  // onRosterStatusListChange effect.
  const [rosterStatusList, setRosterStatusList] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchDtrCount = async () => {
      if (!effectiveRosterId) {
        setDtrCount(0);
        return;
      }
      try {
        const response = await getDTRListClassRosterIdWise(effectiveRosterId);
        if (cancelled) return;
        if (response?.status === true) {
          setDtrCount((response?.data ?? []).length);
        } else {
          setDtrCount(0);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch DTR count:", error);
        }
      }
    };

    fetchDtrCount();
    return () => {
      cancelled = true;
    };
  }, [effectiveRosterId, dtrRefreshKey]);

  // ASTT-7319: "View DTRs" cross-component jump (item 7) -- ClassDetails.js
  // (via ClassRosterDetailsTable.js's "View DTRs" dialog) and TrainingNotes.js
  // are siblings under this component, not parent-child, so a direct prop/
  // callback between them isn't possible. This lives here instead: a value
  // object (date + a nonce) that changes identity on every jump request,
  // passed down to TrainingNotes.js as a prop it watches via useEffect --
  // the nonce specifically exists so clicking the SAME date twice in a row
  // still triggers that effect (React skips a useEffect whose dependency
  // is reference-equal to last time, which a plain date string alone
  // wouldn't guarantee across two separate clicks with the same value).
  const [jumpToDtrDate, setJumpToDtrDate] = useState(null);

  const handleJumpToDtrDate = (date) => {
    setJumpToDtrDate({ date, nonce: Date.now() });
    // Switch to the Training Notes tab so the user actually sees the
    // jump take effect, regardless of which tab they clicked "View DTRs"
    // from (ClassDetails.js/the Roster Details table is always visible,
    // above the tabs, independent of tabIndex).
    if (isCurrentTabDirty()) {
      setPendingTabIndex(0);
    } else {
      setTabIndex(0);
    }
  };

  // TrainingNotes (tab 0) and HandOffReport (tab 3) unmount when their tab isn't active,
  // which would silently destroy unsaved edits on a tab switch. Each reports its own dirty
  // state via onDirtyChange; switching away from a dirty tab requires confirmation first.
  const [trainingNotesDirty, setTrainingNotesDirty] = useState(false);
  const [handOffDirty, setHandOffDirty] = useState(false);
  const [pendingTabIndex, setPendingTabIndex] = useState(null);

  const isCurrentTabDirty = () =>
    (tabIndex === 0 && trainingNotesDirty) || (tabIndex === 3 && handOffDirty);

  const handleTabChange = (event, option) => {
    if (isCurrentTabDirty()) {
      setPendingTabIndex(option);
      return;
    }
    setTabIndex(option);
  };

  const confirmDiscardAndSwitchTab = () => {
    // Leaving on purpose — clear whichever tab's dirty flag is blocking the
    // switch so re-entering it later doesn't immediately re-trigger this.
    if (tabIndex === 0) setTrainingNotesDirty(false);
    if (tabIndex === 3) setHandOffDirty(false);
    setTabIndex(pendingTabIndex);
    setPendingTabIndex(null);
  };

  const cancelTabSwitch = () => {
    setPendingTabIndex(null);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchConfigValue = async () => {
      try {
        const response = await getConfigurationValues(
          "appsettings:lpClassRosterGridV2",
        );

        if (cancelled) return;

        if (response?.statusCode === 200) {
          if (!cancelled) {
            setClassRosterGridV2(
              response?.data?.lpClassRosterGridV2?.toString() === "1",
            );
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch config value:", error);
        }
      }
    };

    fetchConfigValue();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleChangePanel = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid container sx={{ backgroundColor: "#ffffff" }}>
        <Grid item xs={12}>
          <Accordion
            expanded={expanded === "panel1"}
            onChange={handleChangePanel("panel1")}
          >
            <AccordionSummary
              aria-controls="panel1bh-content"
              id="panel1bh-header"
              expandIcon={<ExpandMoreIcon />}
            >
              <p className={classes.headerText}>Class Details</p>
            </AccordionSummary>
            <AccordionDetails>
              <DTRClassDetails
                rosterId={classRosterId}
                setNewClassRosterId={setNewClassRosterId}
                isEditable={location.state?.isEditable || false}
                onClassRequestIdChange={setClassRequestId}
                onJumpToDtrDate={handleJumpToDtrDate}
                dtrRefreshKey={dtrRefreshKey}
                // ASTT-7178: for roster-picker color-coding -- only
                // meaningful (and only fetched) while the Hand-off Report
                // tab is actually active, see ClassDetails.js's own effect.
                reportType={reportType}
                isHandOffTabActive={tabIndex === 3}
                // ASTT-7178: same, for Training Notes -- DTR-Date-based
                // instead of ReportTypeId-based, see ClassDetails.js's
                // own shared effect for how both are handled uniformly.
                dtrDate={selectedDtrDate}
                isTrainingNotesTabActive={tabIndex === 0}
                // ASTT-7178: handOffRefreshKey passed down alongside
                // the existing dtrRefreshKey above -- only the RELEVANT
                // one can actually change while its own tab is mounted
                // (the other tab's component isn't even rendered), so no
                // cross-mode interference in practice; see
                // ClassDetails.js's own effect.
                handOffRefreshKey={handOffRefreshKey}
                onRosterStatusListChange={setRosterStatusList}
              ></DTRClassDetails>
            </AccordionDetails>
          </Accordion>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ width: "100%", backgroundColor: "#ffffff" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                TabIndicatorProps={{
                  sx: {
                    backgroundColor: "#3299FF",
                    height: "5px",
                  },
                }}
                sx={{
                  backgroundColor: "#ffffff",
                  "& .MuiTab-root": {
                    ...fontStyles,
                    fontSize: "14px",
                    fontWeight: "bold",
                    textTransform: "none",
                    minWidth: "auto",
                    "&.Mui-selected": {
                      color: "#3299FF",
                    },
                    "&:hover": {
                      color: "#3299FF",
                    },
                  },
                }}
              >
                <Tab
                  label={
                    // ASTT-7319 follow-up: MUI's Badge component replaced
                    // entirely with a plain flex row -- Badge is designed
                    // for corner-attachment to icons (its absolute
                    // positioning deliberately overlaps the corner of
                    // whatever it wraps), not for a "text, then a gap,
                    // then a count" layout. Confirmed via a second
                    // screenshot that even MUI's own default anchor still
                    // overlapped the text -- fighting Badge's positioning
                    // further wasn't going to get a reliable result. A
                    // flex row with gap gives an explicit, guaranteed
                    // space between the label and the count instead.
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      Training Notes
                      {dtrCount > 0 && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 20,
                            height: 20,
                            px: 0.5,
                            borderRadius: "10px",
                            backgroundColor: "#3299FF",
                            color: "#fff",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                          }}
                        >
                          {dtrCount > 99 ? "99+" : dtrCount}
                        </Box>
                      )}
                    </Box>
                  }
                ></Tab>
                <Tab label="Roster"></Tab>
                <Tab label="Attendance"></Tab>
                <Tab label="Hand-off Report"></Tab>
              </Tabs>
            </Box>
            {/* ASTT-7319 follow-up: inner padding wrapping Training Notes
                and Hand-off Report specifically (the two tabs actively
                worked on this session) -- neither had any padding of
                their own, so their controls sat almost touching the tab
                panel's border. Roster/Attendance left untouched --
                neither has been audited for this same gap yet. */}
            {tabIndex === 0 && (
              <Box sx={{ p: 2 }}>
                <TrainingNotes
                  rosterId={effectiveRosterId}
                  dtrDate={dtrDate}
                  onDirtyChange={setTrainingNotesDirty}
                  classRequestId={classRequestId}
                  jumpToDtrDate={jumpToDtrDate}
                  onDtrSaved={() => setDtrRefreshKey((k) => k + 1)}
                  // ASTT-7178: lifted up from this component's own local
                  // state -- see the declaration above for why. Distinct
                  // from the dtrDate prop just above, which is the ONE-
                  // TIME initial/redirect value only ever used to seed
                  // this default -- selectedDtrDate is the ONGOING
                  // currently-active date, changed by the date picker/
                  // Load/View-DTRs jump, all of which now report back up
                  // through onSelectedDtrDateChange instead of a local setter.
                  selectedDtrDate={selectedDtrDate}
                  onSelectedDtrDateChange={setSelectedDtrDate}
                />
              </Box>
            )}

            {tabIndex === 1 &&
              (classRosterGridV2 ? (
                <RosterDetailsV2
                  key={effectiveRosterId} // Add key to force remount when ID changes
                  classRosterId={effectiveRosterId}
                />
              ) : (
                <RosterDetails
                  classRosterId={effectiveRosterId}
                />
              ))}
            {tabIndex === 2 && (
              <Attendance classRosterId={effectiveRosterId} />
            )}
            {tabIndex === 3 && (
              <Box sx={{ p: 2 }}>
                <HandOffReport
                  rosterId={effectiveRosterId}
                  onDirtyChange={setHandOffDirty}
                  classRequestId={classRequestId}
                  // ASTT-7178: lifted up from this component's own local
                  // state -- see the declaration above for why.
                  reportType={reportType}
                  onReportTypeChange={setReportType}
                  onHandOffSaved={() => setHandOffRefreshKey((k) => k + 1)}
                  rosterStatusList={rosterStatusList}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={pendingTabIndex !== null}
        onClose={cancelTabSwitch}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ...fontStyles,
            // Intentional warning red -- overrides fontStyles' own
            // #3F5765 body-text color, not a mistake.
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          <WarningAmberIcon color="error" />
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          {/* ASTT-7319: was "Urbanist, Arial, sans-serif" -- missing
              "Verdana" compared to every other font-family reference in
              this same file, a real inconsistency within one dialog. */}
          <DialogContentText sx={{ ...fontStyles }}>
            {tabIndex === 0
              ? "You have unsaved Training Notes changes (edited text, a pasted image, or an attached file) that haven't been confirmed with Save. Switching tabs now will discard the unsaved text and lose track of any new attachment. Leave anyway?"
              : "You have unsaved Hand-Off Report changes (edited text, a pasted image, or an attached file) that haven't been confirmed with Save. Switching tabs now will discard the unsaved text and lose track of any new attachment. Leave anyway?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            variant="outlined"
            onClick={cancelTabSwitch}
            sx={{
              ...fontStyles,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Stay on This Tab
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDiscardAndSwitchTab}
            sx={{
              ...fontStyles,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Discard and Leave
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
